/**
 * Restyles the Gorgias chat launcher button: swaps its icon for the brand
 * icon and flattens its gradient/shadow background to solid white. Gorgias
 * renders the button inside a same-origin, src-less iframe (#chat-button),
 * so it's reachable from the parent document, but its CSS-in-JS re-renders
 * it on every state change (opening/closing the chat replaces the whole
 * iframe element, not just its contents), so a one-time fix doesn't stick.
 * Three layers keep it applied with as little visible flash as possible:
 * a MutationObserver on the main document catches the iframe being
 * replaced, a nested MutationObserver on the iframe's own document catches
 * in-place re-renders, and a fast poll is the fallback for anything both
 * observers miss. A "data-lorva-override" flag on the current button
 * element makes re-application a no-op once it's already styled (only set
 * once the icon layer was actually found and swapped, so a button that
 * renders in stages - shell first, icon a tick later - gets retried
 * instead of being marked done too early), so this can never loop against
 * its own DOM writes.
 * This depends on Gorgias's current internal markup, not a supported API,
 * so it can silently stop working after a Gorgias app update.
 *
 * It also pins the button's own size to a fixed 60px/50px split at the
 * theme's usual 990px breakpoint. Gorgias computes this size itself, but
 * not as a simple function of viewport width - measured live, it goes 50
 * (<768) / 60 (768-929) / 50 (929-992) / 60 (>=992), which reads like some
 * internal device-tier heuristic rather than a deliberate breakpoint.
 * Chasing that with the Smile.io button (see smile-widget-size-override.js)
 * isn't practical, so both buttons are forced to the same deterministic
 * 990px split instead of mirroring Gorgias's native behavior. This is done
 * with a `transform: scale()` on the whole button rather than overriding
 * its width/height - the decorative layers and icon inside are positioned
 * to match Gorgias's OWN chosen size (absolute, with offsets baked in for
 * that specific size), so forcing just the outer width/height left them
 * mis-sized and off-center inside it. Scaling the whole element keeps
 * everything inside proportional and centered, whatever size Gorgias
 * currently thinks it rendered at.
 */
(function () {
  var ICON_SVG =
    '<svg width="28" height="28" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">' +
    '<path d="M2.5 11.2422C2.5 7.10436 5.84567 3.75 10.0008 3.75H19.9991C24.1418 3.75 27.5 7.11848 27.5 11.2422V26.25H10.0008C5.85824 26.25 2.5 22.8815 2.5 18.7579V11.2422ZM17.5 13.75V16.25H20V13.75H17.5ZM10 13.75V16.25H12.5V13.75H10Z" fill="#6068B2"/>' +
    "</svg>";

  var POLL_MS = 150;
  var SIZE_BREAKPOINT = 990;
  var SIZE_LARGE = 60;
  var SIZE_SMALL = 50;

  function applySize(btn) {
    var target = window.innerWidth >= SIZE_BREAKPOINT ? SIZE_LARGE : SIZE_SMALL;
    // `width` isn't affected by a transform we applied earlier, so this
    // always reads Gorgias's own current (unscaled) size, whatever it is.
    var nativeWidth = parseFloat(getComputedStyle(btn).width);
    if (!nativeWidth) return;

    var transformValue = "scale(" + (target / nativeWidth).toFixed(4) + ")";
    if (btn.style.getPropertyValue("transform") === transformValue) return;

    btn.style.setProperty("transform", transformValue, "important");
    // The button is anchored bottom-right (fixed, with bottom/right
    // offsets) - scale from that corner so it doesn't visibly shift.
    btn.style.setProperty("transform-origin", "bottom right", "important");
  }

  function applyOverride(doc) {
    var btn = doc.getElementById("gorgias-chat-messenger-button");
    if (!btn || btn.getAttribute("data-lorva-override") === "true") return;

    var iconApplied = false;

    Array.prototype.forEach.call(btn.children, function (child) {
      if (child.querySelector("svg")) {
        child.innerHTML = ICON_SVG;
        iconApplied = true;
      } else {
        child.style.setProperty("background", "#ffffff", "important");
        child.style.setProperty("background-image", "none", "important");
        // One of these layers ships with a `filter: blur(...)` meant to
        // soften its original translucent gradient - left in place, it
        // blurs our opaque white fill into a large soft halo around the
        // button instead of a crisp circle.
        child.style.setProperty("filter", "none", "important");
      }
    });

    btn.style.setProperty(
      "box-shadow",
      "2px 2px 8px 0 rgba(51, 47, 33, 0.06)",
      "important"
    );

    // Only mark done once the icon layer was actually there to swap -
    // Gorgias can insert the button before its child layers exist, and
    // marking it done too early would skip fixing those layers for good.
    if (iconApplied) {
      btn.setAttribute("data-lorva-override", "true");
    }
  }

  var observedDoc = null;

  function applyAll(doc) {
    applyOverride(doc);
    var btn = doc.getElementById("gorgias-chat-messenger-button");
    if (btn) applySize(btn);
  }

  function watchIframeDocument(doc) {
    applyAll(doc);
    if (observedDoc === doc) return;
    var target = doc.body || doc.documentElement;
    if (!target) return;
    observedDoc = doc;
    var observer = new MutationObserver(function () {
      applyAll(doc);
    });
    observer.observe(target, { childList: true, subtree: true });
  }

  function checkIframe() {
    var iframe = document.getElementById("chat-button");
    if (!iframe) return;
    var doc;
    try {
      doc = iframe.contentDocument;
    } catch (e) {
      return;
    }
    if (!doc) return;
    watchIframeDocument(doc);
  }

  // Opening/closing the chat replaces the #chat-button iframe element
  // itself, so a per-document observer alone can miss it - watch the main
  // document too, to react the instant a (new) iframe shows up.
  var topObserver = new MutationObserver(checkIframe);
  topObserver.observe(document.body, { childList: true, subtree: true });

  checkIframe();
  setInterval(checkIframe, POLL_MS);
})();
